<template>
  <section ref="statsSection" class="stats" aria-label="Weekly team statistics">
    <dl class="stats__list">
      <div
        v-for="stat in stats"
        :key="stat.label"
        class="stats__item"
        :class="{ 'stats__item--contributions': stat.key === 'contributions' }"
      >
        <dt>{{ stat.label }}</dt>
        <dd>{{ formatValue(displayValues[stat.key]) }}</dd>
      </div>
    </dl>
  </section>
</template>

<script>
import statsData from '@/data/stats.json';

let hasAnimatedThisSession = false;

export default {
  name: 'StatsSection',

  data() {
    return {
      stats: statsData,
      displayValues: Object.fromEntries(
        statsData.map(({ key, value }) => [key, value === null ? null : 0]),
      ),
      hasAnimated: false,
      observer: null,
      animationFrames: [],
    };
  },

  async mounted() {
    await this.fetchLiveStats();

    if (hasAnimatedThisSession || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showFinalValues();
      return;
    }

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.hasAnimated) this.animateValues();
    }, { threshold: 0.35 });
    this.observer.observe(this.$refs.statsSection);
  },

  beforeUnmount() {
    this.observer?.disconnect();
    this.animationFrames.forEach((frame) => cancelAnimationFrame(frame));
  },

  methods: {
    async fetchLiveStats() {
      const liveValues = {};

      try {
        const response = await fetch('https://api.github.com/users/0xeightnine', {
          headers: {
            Accept: 'application/vnd.github+json',
          },
        });

        if (!response.ok) {
          throw new Error(`GitHub API request failed with status ${response.status}`);
        }

        const user = await response.json();
        Object.assign(liveValues, {
          repositories: user.public_repos ?? 1,
          followers: user.followers ?? 12,
          following: user.following ?? 32,
        });
      } catch (error) {
        console.warn('Unable to load live GitHub profile stats.', error);
      }

      try {
        const response = await fetch(
          'https://github-contributions-api.jogruber.de/v4/0xeightnine?y=last',
        );
        if (!response.ok) {
          throw new Error(`GitHub contributions request failed with status ${response.status}`);
        }

        const graph = await response.json();
        if (
          !Array.isArray(graph.contributions)
          || graph.contributions.some(({ date, count }) => (
            typeof date !== 'string'
            || !/^\d{4}-\d{2}-\d{2}$/.test(date)
            || !Number.isInteger(count)
            || count < 0
          ))
        ) {
          throw new Error('GitHub contributions response has an invalid format');
        }

        const today = new Date();
        const formatDate = (date) => [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, '0'),
          String(date.getDate()).padStart(2, '0'),
        ].join('-');
        const todayDate = formatDate(today);
        today.setDate(today.getDate() - today.getDay());
        const weekStartDate = formatDate(today);

        liveValues.contributions = graph.contributions.reduce((total, day) => (
          day.date >= weekStartDate && day.date <= todayDate ? total + day.count : total
        ), 0);
      } catch (error) {
        console.warn('Unable to load weekly GitHub contributions.', error);
      }

      this.stats = this.stats.map((stat) => ({
        ...stat,
        value: liveValues[stat.key] ?? stat.value,
      }));
      this.displayValues = Object.fromEntries(
        this.stats.map(({ key, value }) => [key, value === null ? null : 0]),
      );
    },
    formatValue(value) {
      return value === null || value === undefined
        ? '—'
        : new Intl.NumberFormat().format(value);
    },
    showFinalValues() {
      this.stats.forEach(({ key, value }) => { this.displayValues[key] = value; });
    },
    animateValues() {
      this.hasAnimated = true;
      hasAnimatedThisSession = true;
      this.observer?.disconnect();
      this.stats.forEach((stat) => this.animateStat(stat));
    },
    animateStat({ key, value }) {
      if (!Number.isFinite(value)) {
        this.displayValues[key] = null;
        return;
      }
      if (value === 0) {
        this.displayValues[key] = 0;
        return;
      }

      const duration = Math.min(22000, Math.max(3750, (3000 + (800000 / value)) * 1.2));
      const startTime = performance.now();
      const update = (time) => {
        const progress = Math.min((time - startTime) / duration, 1);
        this.displayValues[key] = Math.round(value * (1 - ((1 - progress) ** 3)));
        if (progress < 1) this.animationFrames.push(requestAnimationFrame(update));
      };
      this.animationFrames.push(requestAnimationFrame(update));
    },
  },
};
</script>
